<template>
    <div class="tw-tickets tw-stage tw-stage--padded">
        <h3 v-if="dmnOptions.widgetMode === 'default-view'" class="tw-text--center" v-bind:class="{ 'tw-text--flush': sellableTickets.length > 5 }">
            <span v-if="cartManager.presaleCode">
                Presale Tickets<br>
            </span>
            {{ selectedOccurrence.date_friendly }}
        </h3>

        <p class="tw-text--center">
            <button type="button" v-if="goBackButtonDisplay('timeSlots')" v-on:click="showTickets = false; filterSellableTickets('All', true)" v-bind:class="'tw-btn tw-btn--default'">
                <font-awesome-icon icon="chevron-left" />
                <span>&nbsp; Go Back </span>
            </button>

            <previous-stage v-if="goBackButtonDisplay()" class="tw-grid__col--25" v-bind:twStyle="'tw-btn tw-btn--default'" v-bind:buttonStage='"tickets"'></previous-stage>
        </p>

        <div v-if="totalSellableTickets > 5 && dmnOptions.widgetMode === 'list-view'" class="tw-grid__col--25 tw-responsive__xs--block tw-responsive__xs--gutter tw-responsive__xxs--block tw-responsive__xxs--gutter">
            <buy-now-button ref="buyNowScroll" v-bind:buttonPosition='"tickets-button-top"' v-bind:toggleSticky="toggleStickyButton"></buy-now-button>
        </div>

        <div v-if="showCheckoutError" class="tw-items tw-responsive__xs--half-padding tw-responsive__xxs--half-padding">
            <div class="tw-item tw-item--danger">
            <p class="tw-text--center" v-if="!checkoutErrorMessage">There was an issue processing your request, please try again. If the issue persists, please contact support@designmynight.com for assistance with purchasing your tickets!</p>
            <p class="tw-text--center" v-if="checkoutErrorMessage">{{checkoutErrorMessage}}</p>
            </div>
        </div>

        <div v-if="sellableStartTimes.total > 1 && promotionsRetrieved" class="tw-tickets__filter">
            <p class="tw-text--flush tw-text--center">
                <font-awesome-icon :icon="['far', 'clock']" />
                {{showTickets ? 'Filter by start time:' : 'Choose a start time:'}}
            </p>
            <ul class="tw-list--styleless tw-list--inline tw-list--center">
                <sellable-time-slots
                    v-for="(active, timeSlot) in sellableStartTimes.options"
                    :key="timeSlot"
                    v-bind:timeSlot="timeSlot"
                    v-bind:isSoldOut="active.soldOut"
                    v-bind:standalone="!showTickets"
                >
                </sellable-time-slots>
            </ul>
        </div>

        <div v-if="totalSellableTickets && promotionsRetrieved" v-bind:style="{ 'display': showTickets ? 'block' : 'none' }">
            <div class="tw-items tw-responsive__xs--half-padding tw-responsive__xxs--half-padding">
                <ticket v-for="(ticketType, ticketTypeId) in sellableTickets" :key="ticketType.id" v-bind:ticketType="ticketType" v-bind:ticketTypeId="ticketTypeId" v-bind:filtered="ticketType.filtered" v-bind:promotionReduction="adjustTicketPriceForPromotions(ticketTypeId)"></ticket>
                <div class="tw-items tw-responsive__xs--half-padding tw-responsive__xxs--half-padding" v-if="errorMessage">
                    <div class="tw-item tw-item--danger tw-item--danger--fill tw-item--split tw-responsive__xs--half-padding tw-responsive__xxs--half-padding">
                        <font-awesome-icon icon="exclamation-triangle" />
                        {{ errorMessage }}
                    </div>
                </div>
            </div>
            <div class="tw-grid__row tw-responsive__xs--half-padding tw-responsive__xxs--half-padding">
                <div class="tw-grid__col--25 tw-responsive__xs--block tw-responsive__xs--gutter tw-responsive__xxs--block tw-responsive__xxs--gutter">
                    <previous-stage v-if="dmnOptions.widgetMode === 'default-view' && showGoBackButton" class="tw-responsive__xs--hide" v-bind:twStyle="'tw-btn tw-btn--default tw-btn--lg tw-btn--block'" v-bind:buttonStage='"tickets"'></previous-stage>
                </div>
                <div class="tw-grid__col--50 tw-text--right tw-text--xlg tw-text--tall tw-responsive__xs--block tw-responsive__xs--center tw-responsive__xxs--block tw-responsive__xxs--center">
                    {{ cartManager.getTotalQuantitySelected() }} Tickets
                    <span v-if="selectedCost > 0">
                        ({{ cartManager.getTotalCostOfSelected() - this.adjustTotalPriceForPromotions() | currency }})
                    </span>
                    <p v-if="this.adjustTotalPriceForPromotions() > 0" class="tw-text tw-text--sm tw-text--small">(You save {{ this.adjustTotalPriceForPromotions() | currency }})</p>
                </div>
                <div class="tw-grid__col--25 tw-responsive__xs--block tw-responsive__xs--gutter tw-responsive__xxs--block tw-responsive__xxs--gutter">
                    <buy-now-button v-bind:buttonPosition='"tickets-button"'></buy-now-button>
                </div>
            </div>
        </div>
        <div v-if="!promotionsRetrieved">
            <div class="tw-items tw-responsive__xs--half-padding tw-responsive__xxs--half-padding">
                <loading-splash></loading-splash>
            </div>
        </div>
        <div v-if="!totalSellableTickets" class="tw-items tw-responsive__xs--half-padding tw-responsive__xxs--half-padding">
            <div class="tw-item tw-item--muted">
                <p class="tw-text--center">{{ selectedOccurrence.showMessage(this.selectedEvent.timezone) }}</p>
            </div>
        </div>
    </div>
</template>

<script lang='ts'>
    import { Vue } from "vue-property-decorator";
    import { Component } from "vue-property-decorator";
    import moment from 'moment-timezone';
    import CartManager from '../../../services/CartManager';
    import DataProviderService from "../../../services/DataProviderService";

    // Components
    import BuyNowButton from '../../misc/buy-now-button/BuyNowButton.vue';
    import LoadingSplash from '../../misc/loading-splash/LoadingSplash.vue';
    import PreviousStage from '../../misc/previous-stage/PreviousStage.vue';
    import SellableTimeSlots from '../sellable-time-slots/SellableTimeSlots.vue';
    import Ticket from '../ticket/Ticket.vue';
    import TicketWidgetComponent from '../../TicketWidgetComponent';

    // Models
    import Event from '../../../models/Event';
    import Occurrence from '../../../models/Occurrence';
    import PromotionModel from '../../../models/Promotion';
    import TicketType from '../../../models/TicketType';

    //fontawesome
    import { library } from '@fortawesome/fontawesome-svg-core';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
    import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
    import { faClock } from '@fortawesome/free-regular-svg-icons';
    import Promotion from "../../../models/Promotion";

    library.add(faClock, faExclamationTriangle);

    declare var TICKETS_CHECKOUT: string;

    @Component({
        components: {
            BuyNowButton,
            PreviousStage,
            SellableTimeSlots,
            Ticket,
            FontAwesomeIcon,
            LoadingSplash
        }
    })

    export default class Tickets extends TicketWidgetComponent {
        sellableTickets: any = {};
        sellableStartTimes: any = {
          options: {},
          total: null
        };
        showTickets: boolean = true;
        totalSellableTickets: any = 0;
        errorMessage: string = '';
        selectedCost: number = 0;
        filteredCount: number = 0;
        toggleStickyButton: boolean = false;
        showGoBackButton: boolean = false;
        promotionsRetrieved: boolean = false;

        mounted() {
            this.setUp();
        }

        setUp() {
            // if there is an occurrence and event, then set up tickets
            if(this.selectedOccurrence && this.selectedEvent) {

                if (this.selectedEvent.require_time_slot_selection) {
                    this.showTickets = false;
                }

                this.setUpTickets();
                this.getPromotionData();
                this.handleGTM();
            }

            // add listener for updating the cart
            this.$root.$on('updateCart', () => {
                this.cartUpdated();
            });

            // add listener for selecting timeSlots
            this.$root.$on('updateSelectedTimeSlots', (timeSlot: string, active: boolean) => {
                this.filterSellableTickets(timeSlot, active);

                this.showTickets = true;
            });

            // if we have the calendar stage, show go back
            this.showGoBackButton = this.$root.$data.stages['calendar'] ? true : false;

            window.addEventListener('scroll', this.scrollFunction);
        }

        /**
         * @desc filters tickets based on which timeslots have been selected
         * @param {string} - timeSlot
         * @param {boolean} - active
         */
        filterSellableTickets(timeSlot: string, active: boolean) {
            this.filteredCount = active ? this.filteredCount + 1 : this.filteredCount - 1;
            this.filteredCount = timeSlot === 'All' ? 0 : this.filteredCount;

            // update the existing sellableStartTimes
            this.sellableStartTimes.options[timeSlot].active = active;

            // if filteredCount > 0 then we are filtering down the tickets
            if(this.filteredCount > 0 && timeSlot !== 'All') {

                // get each key in the sellableTickets
                for(let key in this.sellableTickets) {

                    // get each time from the sellableStartTimes
                    for(let time in this.sellableStartTimes.options) {

                        // if the ticket start time is the same as the timeSlot
                        if(this.sellableTickets[key].start_time === time) {

                            // send an event with wether the ticket should be filtered out or in with the ticketId
                            this.$root.$emit('displayTicket', this.sellableStartTimes.options[time].active, key, timeSlot);
                        }
                    }
                }
            }
            else if(timeSlot === 'All') {
                this.$root.$emit('resetTimeSlots');

                for(let key in this.sellableTickets) {
                    this.$root.$emit('displayTicket', true, key);
                }

                // get each time from the sellableStartTimes
                for(let time in this.sellableStartTimes.options) {
                    this.sellableStartTimes.options[time].active = false;
                }
            }
            // if the filteredCount is 0, then we should display all the tickets
            else {
                for(let key in this.sellableTickets) {
                    this.$root.$emit('displayTicket', true, key);
                }
            }
        }

        /**
         * @desc get the tickets price text
         * @param {string} - ticketId
         * @return {string}
         */
        getTicketTypePriceText(ticketId: string) : string {
            let ticketType = this.selectedEvent.getTicketType(ticketId);

            return ticketType.price;
        };

        /**
         * @desc get the tickets description and format as needed
         * @param {string} - ticketId
         * @return {string}
         */
        getTicketDesc(ticketId: string) : string {
            let ticketType = this.selectedEvent.getTicketType(ticketId);
            let description: any = {
              fullText: '',
              showExtender: false,
              showFull: true
            };

            if(ticketType && ticketType.description) {
                description.fullText = ticketType.description;

                if(description.fullText.length > 100) {
                    let trimmedString = description.fullText.substr(0, 80);

                    // don't split in middle of word
                    trimmedString = trimmedString.substr( 0, Math.min(trimmedString.length, trimmedString.lastIndexOf(" ")));
                    description.shortText = trimmedString + '...';
                    description.showExtender = true;
                    description.showFull = false;
                }
            }

            return description;
        };

        /**
         * @desc does the initial set up of tickets for the selected occurrence
         */
        setUpTickets() {
            let sellableTickets:any = this.selectedOccurrence.getSellableTicketTypes();
            let sellableTimeSlots: any[] = [];

            // sort in same order as Event
            this.sellableTickets = {};
            this.totalSellableTickets = 0;

            // ensure we're not in presale
            if(!this.selectedOccurrence.presale_mode_enabled || this.cartManager.hasPresaleCode()) {
                let ticketTypes: TicketType[] = [];

                let lastStartTime = false;
                let now = this.serverTime;

                this.selectedEvent.ticket_types.forEach((ticketType: TicketType) => {
                    let ticketId:any = ticketType.id;

                    if(sellableTickets[ticketId]) {

                        // skip ticket if sold out and we are hiding these tickets
                        if((sellableTickets[ticketId].is_sold_out || !this.cartManager.getAvailableForTicketType(ticketType.id)) && !this.selectedEvent.showSoldOutTicketTypes()) {
                            return;
                        }

                        // check if ticket has passed it's on sell until time, skip (hide) this ticket if it has
                        const sellUntilInvalid = sellableTickets[ticketId].sell_until && now.isAfter(sellableTickets[ticketId].sell_until);
                        if(sellUntilInvalid) {
                            return;
                        }

                        // check if ticket has not yet passed it's on sell from time, skip (hide) this ticket if it hasn't
                        const sellFromInvalid = sellableTickets[ticketId].sell_from_invalid = sellableTickets[ticketId].sell_from && now.isBefore(sellableTickets[ticketId].sell_from);
                        if(sellFromInvalid) {
                            return;
                        }

                        let sellableTicketType = new TicketType(JSON.parse(JSON.stringify(sellableTickets[ticketId])));
                        sellableTicketType.id = ticketType.id;

                        // if showing VAT, add the excluding VAT price to the ticket
                        if(this.selectedEvent.display_vat && ticketType.price > 0) {
                            sellableTicketType.price_excluding_vat = this.selectedEvent.getTicketTypePriceExcludingVAT(ticketId);
                        }

                        // add description break down
                        sellableTicketType.desc = this.getTicketDesc(ticketId);

                        // show all at start
                        sellableTicketType.filtered = true;

                        // min per purchase
                        sellableTicketType.min_tickets_per_purchase = ticketType.min_tickets_per_purchase || 1;

                        // max per purchase
                        sellableTicketType.max_tickets_per_purchase = ticketType.max_tickets_per_purchase || 99;

                        // set bundled ticket types
                        sellableTicketType.bundle_ticket_type = ticketType.bundle_ticket_type || "";

                        if(sellableTicketType.bundle_ticket_type.length) {
                            let soldOut: boolean[] = [];
                            sellableTicketType.bundle_ticket_type.forEach((bundle_ticket_type: string) => {
                                let bundledTicketParent = this.selectedEvent.getTicketType(bundle_ticket_type) || "";

                                soldOut.push(bundledTicketParent.is_sold_out || this.cartManager.getAvailableForTicketType(bundledTicketParent.id) === 0);
                                sellableTicketType.bundle_ticket_title.push(bundledTicketParent.ticket_type);
                            });
                            sellableTicketType.bundle_ticket_parent_sold_out = soldOut.every(element => element);
                        }

                        // set which tickets should show start time
                        let startTime = this.selectedEvent.getTicketTypeStartTime(ticketId);
                        sellableTicketType.start_time = startTime;

                        ticketTypes.push(sellableTicketType);

                        if(lastStartTime !== startTime) {
                            lastStartTime = startTime;
                            sellableTicketType.show_start_time = true;

                            // build list of all start times
                            sellableTimeSlots.push(startTime);
                        }

                        this.totalSellableTickets++;
                    }
                });

                let soldOutTicketTypes = ticketTypes.filter((ticketType: TicketType) => {
                    return ticketType.is_sold_out || !this.cartManager.getAvailableForTicketType(ticketType.id) || ticketType.bundle_ticket_parent_sold_out;
                });

                let availableTicketTypes = ticketTypes.filter((ticketType: TicketType) => {
                    return !ticketType.is_sold_out && this.cartManager.getAvailableForTicketType(ticketType.id) && !ticketType.bundle_ticket_parent_sold_out;
                });

                ticketTypes = !this.selectedEvent.hide_sold_out_tickets ? availableTicketTypes.concat(soldOutTicketTypes) : availableTicketTypes;

                ticketTypes.forEach((ticketType: TicketType) => {
                    this.sellableTickets[ticketType.id] = ticketType;
                    this.sellableTickets[ticketType.id].remaining = this.cartManager.getAvailableForTicketType(ticketType.id);
                    this.sellableTickets[ticketType.id].custom_ticket_labels = this.selectedEvent.getTicketType(ticketType.id).custom_ticket_labels || [];
                });

                // set up sellable time slot filter
                sellableTimeSlots.sort();

                this.sellableStartTimes.total = sellableTimeSlots.length;

                sellableTimeSlots.forEach((timeSlot:any) => {
                    this.sellableStartTimes.options[timeSlot] = {
                        active: false,
                        soldOut: this.isTimeSlotSoldOut(timeSlot)
                    };
                });

                if(Object.keys(this.sellableStartTimes.options).length === 1) {
                    this.showTickets = true;
                }

                if(this.selectedEvent.require_time_slot_selection) {
                    this.sellableStartTimes.options['All'] = {
                        active: false,
                        soldOut: false
                    };
                }
            }
        };

        /**
         * @param {string} timeSlot
         * @return {boolean}
         */
        isTimeSlotSoldOut(timeSlot: string): boolean {
            let tickets = [];

            let isSoldOut = true;

            // get each ticket with the same start time
            for(let key in this.sellableTickets) {
                if(this.sellableTickets[key].start_time === timeSlot) {
                    tickets.push(this.sellableTickets[key]);
                }
            }

            // check at least one is not sold out
            for(let i = 0; i < tickets.length; i++) {
                if(!tickets[i].is_sold_out && tickets[i].remaining > 0) {
                    return false;
                }
            }

            return isSoldOut;
        }

        /**
         * @desc redirects the user to the checkout
         */
        cartUpdated() {
            this.selectedCost = this.cartManager.getTotalCostOfSelected();
            this.setUpTickets();
            this.setUpPromotions();
        }

        /**
         * @desc activates the buy now button to become sticky when scrolling
         */
        scrollFunction() {
            let buyNowScroll: any = this.$refs.buyNowScroll;

            if(buyNowScroll && buyNowScroll.$el && window.scrollY > buyNowScroll.$el.offsetTop) {
                this.toggleStickyButton = true;
            }
            else {
                this.toggleStickyButton = false;
            }
        }

        /**
         * @desc requests Promotion Data from the dataProviderService and saves them to cache
         * @return {Promise}
         */
        getPromotionData() {
            return this.dataProviderService.getPromotionData(this.selectedEvent._id, this.selectedOccurrence.occurrenceMoment.format('YYYY-MM-DD[T]HH:mm:ss')).then(
                (results: any) => {
                    this.$root.$data.promotions[this.selectedOccurrence.occurrenceMoment.format('YYYYMMDD')] = [];

                    for(let i = 0; i < results.length; i++) {
                        // foreach promotion, make a new promotion model and assign to ticket types
                        if(results[i].promotion) {
                            let promotion = new PromotionModel(results[i].promotion);

                            this.$root.$data.promotions[this.selectedOccurrence.occurrenceMoment.format('YYYYMMDD')].push(promotion);
                        }
                    }

                    this.promotionsRetrieved = true;
                    this.setUpPromotions();
                },
                (error: any) => {
                    // fail silently
                    this.promotionsRetrieved = true;
                 }
            );
        }

        /**
         * @desc loops through promotions that are available for a date and applies them to the correlating tickets
         */
        setUpPromotions() {
            for(let ticketId in this.sellableTickets) {
                if(this.sellableTickets.hasOwnProperty(ticketId)) {
                    const promotion = this.findPromotionForTicket(ticketId);

                    if(promotion) {
                        this.sellableTickets[ticketId].promotion = promotion;
                    }
                }
            }
        }

        /**
         * @desc gets the selected quantity of each ticket and then reduces the display price based on the promotions rules
         * @return {number}
         */
        adjustTicketPriceForPromotions(ticketId: string) {
            let selectedQuantities = this.cartManager.selectedQuantities;
            let ticketType = this.selectedEvent.getTicketType(ticketId);
            let totalDiscount = this.calculateAppliedPromotionAmount(ticketId, ticketType, selectedQuantities[ticketId]);

            return totalDiscount;
        }

        /**
         * @desc gets the selected quantity of each ticket and then reduces the display price based on the promotions rules
         * @return {number}
         */
        adjustTotalPriceForPromotions() {
            let selectedQuantities = this.cartManager.selectedQuantities;
            let totalDiscount = 0;

            // get each ticket quantity selected
            for(let ticketId in selectedQuantities) {
                let ticketType = this.selectedEvent.getTicketType(ticketId);

                totalDiscount += this.calculateAppliedPromotionAmount(ticketId, ticketType, selectedQuantities[ticketId]);
            }

            return totalDiscount;
        }

        calculateAppliedPromotionAmount(ticketId: string, ticketType: any, selectedQuantity: number): number {
            // if there is a promotion on that ticket
            if(this.sellableTickets[ticketId] && this.sellableTickets[ticketId].promotion) {
                let qualifyingQuantity = this.sellableTickets[ticketId].promotion.qualifying_quantity;
                let applyToQuantity = this.sellableTickets[ticketId].promotion.apply_to_quantity;
                let totalSelected = selectedQuantity;
                let totalTicketsDiscounted = 0;

                if(totalSelected >= qualifyingQuantity) {
                    const timesToApplyPromotion = Math.floor(totalSelected / qualifyingQuantity);

                    totalTicketsDiscounted = applyToQuantity * timesToApplyPromotion;
                }

                return totalTicketsDiscounted * ticketType.price;
            }

            return 0;
        }

        goBackButtonDisplay(buttonType: string) {

            if(buttonType === 'timeSlots') {
                return this.selectedEvent.require_time_slot_selection
                    && this.showTickets
                    && this.sellableStartTimes.total > 1
                    && this.dmnOptions.widgetMode === 'default-view'
                    && this.showGoBackButton;
            }

            if(buttonType !== 'timeSlots'
                && this.selectedEvent.require_time_slot_selection
                && !this.showTickets
                && this.sellableStartTimes.total > 1
                && this.dmnOptions.widgetMode === 'default-view'
                && this.showGoBackButton
            ) {
                return true;
            }

            if(buttonType !== 'timeSlots'
                && this.selectedEvent.require_time_slot_selection
                && this.dmnOptions.widgetMode === 'default-view'
                && this.sellableStartTimes.total === 1
                && this.showGoBackButton
            ) {
                return true;
            }

            if(buttonType !== 'timeSlots'
                && !this.selectedEvent.require_time_slot_selection
                && this.dmnOptions.widgetMode === 'default-view'
                && this.showGoBackButton
            ) {
                return true;
            }

            return false;
        }

        /**
         * @param {string} ticketId
         * @return {object|undefined}
         * @private
         */
        private findPromotionForTicket(ticketId: string) {
          let promotions = this.$root.$data.promotions[this.selectedOccurrence.occurrenceMoment.format('YYYYMMDD')];

            if(promotions.length > 0) {

                promotions = promotions.filter(
                    (promotion: Promotion) => this.checkRestrictions(promotion, ticketId)
                );

              const highestPromotion = this.getHighestPromotion(promotions);

              const qualifyingPromotions = this.getQualifyingPromotions(promotions, highestPromotion, ticketId);

              if (qualifyingPromotions.length) {
                return this.getHighestPromotion(qualifyingPromotions)
              }
            }

            return false;
        }

      /**
       * returns all the promotions that qualify based on quantity of tickets
       * @param {Promotion[]} promotions
       * @param {Promotion} highestPromotion
       * @param {string} ticketId
       */
        getQualifyingPromotions(promotions: Promotion[], highestPromotion: Promotion, ticketId: string): Promotion[] {
          return promotions.filter(
              (promotion: Promotion) => this.checkPromotionQualifies(
                  promotion, ticketId, highestPromotion.qualifying_quantity
              )
          );
        }

      /**
       * returns promotion with highest qualifying quantity from promotions
       * @param {Promotion[]} promotions
       */
      getHighestPromotion(promotions: Promotion[]): Promotion {
          return promotions.reduce(
              (prev: Promotion, current: Promotion) => {
                return prev.qualifying_quantity > current.qualifying_quantity ? prev : current
              }
          );
        }

      /**
       * check we can use the promotion based on the quantity of tickets
       * @param {Promotion} promotion
       * @param {string} ticketId
       * @param {Number} highestQualifyingQuantity
       */
        checkPromotionQualifies(promotion: Promotion, ticketId: string, highestQualifyingQuantity: number) {
          const ticketQuantity: number =
              this.$root.$data.cartManager.selectedQuantities[ticketId] > 0
                  ? this.$root.$data.cartManager.selectedQuantities[ticketId]
                  : 0;

          if (ticketQuantity < highestQualifyingQuantity) {
            return promotion.qualifying_quantity <= ticketQuantity;
          }
          else if (ticketQuantity >= highestQualifyingQuantity) {
            return promotion.qualifying_quantity === highestQualifyingQuantity;
          }
          else {
            return;
          }
        }

      /**
       * check the promotion has no restrictions
       * @param {Promotion} promotion
       * @param {string} ticketId
       */
        checkRestrictions(promotion: Promotion, ticketId: string): boolean {
          return !promotion.sub_type_restrictions
              || promotion.sub_type_restrictions.length === 0
              || promotion.sub_type_restrictions.indexOf(ticketId) >= 0
        }

        private handleGTM() {
            if (!window.dataLayer) {
                return;
            }

            const items: any[] = []

            Object.keys(this.sellableTickets).forEach(
                (ticketId) => {
                    items.push({
                        item_category: "Access Tonic",
                        item_brand: this.selectedEvent.title,
                        item_name: this.sellableTickets[ticketId].name,
                        item_id: `${this.selectedEvent._id}_${ticketId}`,
                        price: this.sellableTickets[ticketId].price || 0
                    })
                }
            );

            const occurrenceMoment = this.selectedOccurrence.occurrenceMoment;
            const eventDate = occurrenceMoment.format('YYYY-MM-DD');
            const dmnReferenceId = this.cartManager.purchaseId || null;

            const itemListId = `${this.selectedEvent._id}_${occurrenceMoment.format('YYYYMMDD')}`;
            const itemListName = `${this.selectedEvent.title} (${eventDate})`;

            // send event on viewing tickets
            window.dataLayer.push({ecommerce: null});  // Clear the previous ecommerce object.
            window.dataLayer.push({
                event: "view_item_list",
                event_date: eventDate,
                dmn_reference_id: dmnReferenceId,
                ecommerce: {
                    item_list_id: itemListId,
                    item_list_name: itemListName,
                    items,
                }
            });
        }
    }
</script>
